/*
 * Wix doesn't put our snippet on the studio's page. It runs it inside its own
 * cross-origin container, so the document we live in has an address like
 * `https://astudio-wixsite-com.filesusr.com/html/<hash>.html`, and that is what
 * analytics reports. The studio's origin is recoverable from in there; the path
 * is not, because Wix blocks every signal that would carry it.
 */

// Not `/`, so container traffic never merges into a studio's homepage numbers.
export const EMBEDDED_PAGE_PATH = '/mindbody-widget'

function canReachTopWindow(): boolean {
  try {
    return typeof window.top?.location.href === 'string'
  } catch {
    // Reading a cross-origin top throws SecurityError, which is the signal that
    // something has framed us across origins.
    return false
  }
}

function resolveStudioOrigin(): string | null {
  let ancestors: string[] = []
  try {
    ancestors = Array.from(window.location.ancestorOrigins ?? [])
  } catch {
    ancestors = []
  }

  // Ordered immediate parent first, top-level last, so the studio's page is the
  // final entry however deeply we are nested.
  const outermost = ancestors[ancestors.length - 1]
  // A sandboxed ancestor without allow-same-origin reports the string "null".
  if (outermost && outermost !== 'null') {
    return outermost
  }

  // Covers browsers without ancestorOrigins: a container's referrer is the page
  // that embedded it.
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin
    } catch {
      return null
    }
  }

  return null
}

// Returns null when the document's own address is already correct.
export function resolveReportablePageUrl(): string | null {
  if (canReachTopWindow()) {
    return null
  }

  const origin = resolveStudioOrigin()
  if (!origin) {
    return null
  }

  return `${origin}${EMBEDDED_PAGE_PATH}`
}
